// ============================================================
// SISTEMA i18n - Pira Notes
// ============================================================
// Context + hook custom (no dipendenze esterne).
// Lingue supportate: it, en, es, fr, tr.
// Default: lingua di sistema (navigator.language).
// Persistenza: localStorage 'pira_lang'.
// ============================================================

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import it from './translations/it.json';
import en from './translations/en.json';
import es from './translations/es.json';
import fr from './translations/fr.json';
import tr from './translations/tr.json';

export type Lang = 'it' | 'en' | 'es' | 'fr' | 'tr';

const translations: Record<Lang, any> = { it, en, es, fr, tr };

export const LANGUAGES: { code: Lang; label: string; flag: string }[] = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
];

const STORAGE_KEY = 'pira_lang';

/** Rileva la lingua di sistema e mappa a una supportata. */
export function detectSystemLang(): Lang {
  if (typeof navigator === 'undefined') return 'en';
  const nav = (navigator.language || 'en').toLowerCase();
  if (nav.startsWith('it')) return 'it';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('tr')) return 'tr';
  return 'en';
}

/** Restituisce la lingua salvata (o system se non salvata). */
function getInitialLang(): Lang {
  if (typeof localStorage === 'undefined') return detectSystemLang();
  const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
  if (saved && translations[saved]) return saved;
  return detectSystemLang();
}

// ------------------------------------------------------------
// Risoluzione chiave nidificata: "nav.notes" -> translations[lang].nav.notes
// ------------------------------------------------------------
function resolve(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj);
}

/** Interpolazione: "Ciao {{name}}" con {name: 'Mario'} -> "Ciao Mario". */
function interpolate(str: string, vars?: Record<string, string | number>): string {
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => String(vars[k] ?? ''));
}

// ------------------------------------------------------------
// Context
// ------------------------------------------------------------
interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);

  // Applica lang all'<html> per accessibilità
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch { /* ignore */ }
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const value = resolve(translations[lang], key);
      if (typeof value === 'string') return interpolate(value, vars);
      // Fallback inglese
      const enValue = resolve(translations.en, key);
      if (typeof enValue === 'string') return interpolate(enValue, vars);
      // Chiave non trovata: ritorna la chiave stessa (debug)
      return key;
    },
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

/** Hook per usare le traduzioni. */
export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n deve essere usato dentro I18nProvider');
  return ctx;
}

/** Hook per ottenere le label dei giorni della settimana tradotte. */
export function useWeekdays() {
  const { t } = useI18n();
  return [
    { value: 1, label: t('days.mon_full'), short: t('days.mon'), color: 'bg-blue-500' },
    { value: 2, label: t('days.tue_full'), short: t('days.tue'), color: 'bg-blue-500' },
    { value: 3, label: t('days.wed_full'), short: t('days.wed'), color: 'bg-blue-500' },
    { value: 4, label: t('days.thu_full'), short: t('days.thu'), color: 'bg-blue-500' },
    { value: 5, label: t('days.fri_full'), short: t('days.fri'), color: 'bg-blue-500' },
    { value: 6, label: t('days.sat_full'), short: t('days.sat'), color: 'bg-emerald-500' },
    { value: 0, label: t('days.sun_full'), short: t('days.sun'), color: 'bg-rose-500' },
  ];
}

/** Preset giorni settimana tradotti. */
export function useWeekdayPresets() {
  const { t } = useI18n();
  return [
    { id: 'all', label: t('reminders.label_all_days'), days: [0, 1, 2, 3, 4, 5, 6] },
    { id: 'weekdays', label: t('reminders.label_weekdays'), days: [1, 2, 3, 4, 5] },
    { id: 'weekend', label: t('reminders.label_weekend'), days: [0, 6] },
  ];
}
